import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Loader } from "@/components/common/Loader";
import { eur } from "@/lib/format";
import { toast } from "sonner";
import { ArrowLeft, Save, Pencil } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const blank = { name: "", city: "", country: "United Kingdom", email: "", phone: "",
  category: "Supplier", payment_terms: "NT30", incoterms: "DAP", currency: "GBP", tax_number: "",
  purch_org: "1000", company_code: "1000", account_group: "KRED", order_currency: "GBP",
  schema_group: "0001", gr_based_iv: true, recon_account: "200000", min_order_value: 0,
  abc_class: "B", plnd_deliv_time_days: 7, vat_reg_no: "" };

export default function VendorDetail() {
  const { code } = useParams();
  const isNew = !code;
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(isNew ? {} : null);
  const [edit, setEdit] = useState(isNew);
  const [form, setForm] = useState(blank);

  useEffect(() => {
    if (isNew) return;
    api.get(`/vendors/${encodeURIComponent(code)}`).then((r) => {
      setVendor(r.data);
      setForm({ ...blank, ...r.data });
    }).catch(() => setVendor(false));
  }, [code, isNew]);

  if (vendor === null) return <Loader label="Loading vendor…" />;
  if (vendor === false) return <div className="p-8 text-[#6a6d70]">Vendor not found.</div>;

  const save = async () => {
    if (!form.name) { toast.error("Vendor name is required"); return; }
    const body = { name: form.name, city: form.city, country: form.country, email: form.email,
      phone: form.phone, category: form.category, payment_terms: form.payment_terms,
      incoterms: form.incoterms, currency: form.currency, tax_number: form.tax_number,
      purch_org: form.purch_org, company_code: form.company_code, account_group: form.account_group,
      order_currency: form.order_currency, schema_group: form.schema_group, gr_based_iv: form.gr_based_iv,
      recon_account: form.recon_account, min_order_value: Number(form.min_order_value || 0),
      abc_class: form.abc_class, plnd_deliv_time_days: Number(form.plnd_deliv_time_days || 0),
      vat_reg_no: form.vat_reg_no };
    try {
      if (isNew) { const { data } = await api.post("/vendors", body); toast.success(`Vendor ${data.vendor_code} created`); setEdit(false); navigate(`/vendors/${encodeURIComponent(data.vendor_code)}`); }
      else { await api.put(`/vendors/${encodeURIComponent(code)}`, body); toast.success("Vendor saved"); setEdit(false); const r = await api.get(`/vendors/${encodeURIComponent(code)}`); setVendor(r.data); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const inp = "mt-1 w-full rounded-md border border-[#c8cfda] bg-white px-2 py-1.5 text-sm outline-none focus:border-[#0D9488] disabled:bg-[#f5f6f7] disabled:text-[#6a6d70]";
  const F = ({ label, k, type = "text" }) => (
    <div><div className="text-[11px] uppercase text-[#6a6d70]">{label}</div>
      <input data-testid={`vendor-${k}`} type={type} disabled={!edit} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={inp} /></div>);

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/purchasing")} data-testid="vendor-back" className="inline-flex items-center gap-1.5 text-sm text-[#0a6ed1] hover:underline"><ArrowLeft size={16} /> Purchasing</button>
      <div className="rounded-lg border border-[#e5e9f0] bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-[#32363a]">{isNew ? "Create Vendor" : vendor.name}</h1>
            <p className="mt-1 text-sm text-[#6a6d70]">Supplier Master · EP-PU03 · <span className="text-[#9aa0a6]">S/4 {isNew ? "XK01" : (edit ? "XK02" : "XK03")}</span>{!isNew && ` · ${vendor.vendor_code}`}</p>
          </div>
          <div className="flex items-center gap-3">
            {!isNew && vendor.spend_tytd != null && <div className="text-right"><div className="text-[11px] uppercase text-[#6a6d70]">Spend YTD</div><div className="text-xl font-semibold text-[#107e3e]">{eur(vendor.spend_tytd)}</div></div>}
            {!isNew && !edit && <button data-testid="vendor-edit-btn" onClick={() => setEdit(true)} className="inline-flex items-center gap-1.5 rounded-md border border-[#c8cfda] bg-white px-4 py-2 text-sm font-semibold text-[#32363a] hover:border-[#0D9488]"><Pencil size={15} /> Edit</button>}
            {edit && <button data-testid="vendor-save-btn" onClick={save} className="inline-flex items-center gap-1.5 rounded-md bg-[#0D9488] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0b7d73]"><Save size={16} /> {isNew ? "Create" : "Save"}</button>}
          </div>
        </div>
        <Tabs defaultValue="general" className="mt-5 border-t border-[#eff1f4] pt-4">
          <TabsList className="bg-[#f7f8fa] border border-[#e5e9f0]">
            <TabsTrigger data-testid="vendor-tab-general" value="general" className="data-[state=active]:bg-white data-[state=active]:text-[#0D9488]">General</TabsTrigger>
            <TabsTrigger data-testid="vendor-tab-purchasing" value="purchasing" className="data-[state=active]:bg-white data-[state=active]:text-[#0D9488]">Purchasing</TabsTrigger>
            <TabsTrigger data-testid="vendor-tab-accounting" value="accounting" className="data-[state=active]:bg-white data-[state=active]:text-[#0D9488]">Accounting</TabsTrigger>
          </TabsList>
          <TabsContent value="general" className="mt-4"><div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <F label="Name" k="name" /><F label="City" k="city" /><F label="Country" k="country" />
            <F label="Email" k="email" /><F label="Phone" k="phone" /><F label="Category" k="category" />
          </div></TabsContent>
          <TabsContent value="purchasing" className="mt-4"><div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <F label="Purchasing Org" k="purch_org" /><F label="Order Currency" k="order_currency" /><F label="Payment Terms" k="payment_terms" />
            <F label="Incoterms" k="incoterms" /><F label="Schema Group" k="schema_group" /><F label="Planned Deliv. (days)" k="plnd_deliv_time_days" />
            <F label="Min Order Value" k="min_order_value" /><F label="ABC Class" k="abc_class" />
            <div><div className="text-[11px] uppercase text-[#6a6d70]">GR-Based IV</div><div className="mt-1 text-sm text-[#32363a]">{form.gr_based_iv ? "Yes" : "No"}</div></div>
          </div></TabsContent>
          <TabsContent value="accounting" className="mt-4"><div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <F label="Company Code" k="company_code" /><F label="Account Group" k="account_group" /><F label="Recon. Account" k="recon_account" />
            <F label="Currency" k="currency" /><F label="VAT Reg No." k="vat_reg_no" /><F label="VAT / Tax No." k="tax_number" />
          </div></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
